<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { api } from '../api/client'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import { EmptyState, ErrorState, Panel, SkeletonRows } from '../components/ui'
import { useAsync, useTitle } from '../lib/hooks'

const route = useRoute()
const id = computed(() => String(route.params.id ?? ''))
useTitle('剧集详情')

const { data, error, initial, reload } = useAsync(
  () => api.catalogSeriesDetail(id.value),
  () => [id.value],
)

const episodeTotal = computed(() =>
  (data.value?.seasons ?? []).reduce((n, s) => n + s.episodes.length, 0),
)
</script>

<template>
  <PageHeader
    :title="data?.title ?? id"
    :breadcrumb="[{ label: '目录', to: '/catalog' }, { label: data?.title ?? id }]"
  />

  <PageBody>
    <Panel v-if="error">
      <ErrorState :message="error.message" @retry="reload" />
    </Panel>
    <SkeletonRows v-else-if="initial" :rows="6" :cols="2" />
    <div v-else-if="data" class="flex flex-col gap-5 sm:flex-row sm:items-start">
      <div class="w-full max-w-[220px] shrink-0 overflow-hidden rounded-lg bg-raised ring-1 ring-line sm:w-[220px]">
        <img
          v-if="data.cover_url"
          :src="data.cover_url"
          :alt="data.title"
          class="aspect-[2/3] w-full object-cover"
        />
        <div v-else class="aspect-[2/3] w-full bg-raised" />
      </div>

      <div class="min-w-0 flex-1 space-y-4">
        <Panel>
          <div class="px-4 py-4">
            <h2 class="text-[20px] font-semibold tracking-tight">{{ data.title }}</h2>
            <p class="mt-2 text-[13px] text-dim">
              ID {{ data.id }} · {{ data.seasons.length }} 季 · {{ episodeTotal }} 集
            </p>
            <p v-if="data.plot" class="mt-3 text-[14px] leading-relaxed text-muted">
              {{ data.plot }}
            </p>
          </div>
        </Panel>

        <Panel title="分集" :hint="`${episodeTotal} 集`">
          <EmptyState v-if="data.seasons.length === 0" title="没有分集" />
          <div v-else class="space-y-4 px-4 py-4">
            <section v-for="season in data.seasons" :key="season.number">
              <h3 class="mb-2.5 text-[13px] font-semibold text-muted">
                {{ season.name || `第 ${season.number} 季` }}
                <span class="ml-1.5 font-normal text-dim">{{ season.episodes.length }} 集</span>
              </h3>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="ep in season.episodes"
                  :key="ep.id"
                  :title="ep.title || `第 ${ep.number} 集`"
                  class="inline-flex items-center gap-1.5 rounded-full border border-line bg-raised px-2.5 py-1.5 text-[13px] font-medium tabular-nums text-text"
                >
                  {{ ep.number }}
                  <span v-if="ep.container" class="text-[11px] font-normal uppercase text-dim">
                    {{ ep.container }}
                  </span>
                </span>
              </div>
            </section>
          </div>
        </Panel>
      </div>
    </div>
  </PageBody>
</template>
